// R115 calendar hub — the PURE rules behind the Calendar page shell
// (client/src/pages/StaysCalendarPage.ts) and its deep link (calendar/link.ts).
// No DOM, no fetch, no storage: the page reads storage / the payload and hands
// plain values in. Tested by lib/calendarCore.test.mjs.
//
//   dates          calendar dates (YYYY-MM-DD), UTC-anchored like the page
//   focus          the one-shot deep link {v:1, view, date, listing_id, stay_id,
//                  turn_id, wo_id, src, at} written to sessionStorage
//                  'stc_focus' by openCalendar() and consumed by the page —
//                  strictly validated (ids ^[\w-]{1,60}$, real dates, a known
//                  view-id shape, ≤ 1 KB, ≤ 10 min old), anything else → null
//   views          resolveView: deep link (transient) → this tab → the user's
//                  explicit default → 'stays'; unknown / forbidden ids fall through
//   lanes          assignLanes / packLanes (moved verbatim from the page)
//   window (B9)    windowQuery / navStep / earlierStep / jumpWindow: the past
//                  is LAZY — the default window starts yesterday; "‹ Earlier"
//                  refetches once with back=30 keeping the visual position;
//                  ‹ › scroll 7 columns inside the loaded range and fetch only
//                  at its edge (the column count never changes on paging)
//   stats          forwardStart + coreStatCounts: window counts over
//                  [max(from, today−1), to] so loading 30 past days never
//                  changes a number (the server's rule, mirrored for an older
//                  server that sends no `filters`)

export const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
export const ID_RE = /^[\w-]{1,60}$/;
export const VIEW_RE = /^[a-z][a-z0-9_]{0,23}(\.[a-z][a-z0-9_]{0,23})?$/;
export const FOCUS_KEY = 'stc_focus';
export const FOCUS_EVENT = 'stc:focus';
export const FOCUS_VERSION = 1;
export const FOCUS_MAX_CHARS = 1024;
export const FOCUS_MAX_AGE_MS = 10 * 60 * 1000;
export const DEFAULT_VIEW = 'stays';
export const NAV_STEP = 7;        // ‹ › move a week
export const BACK_STEP = 30;      // "‹ Earlier" loads 30 more past days
export const MAX_BACK = 60;       // the server accepts back 0..60
export const MAX_DAYS = 120;      // the server's window cap (back + forward)
export const MIN_DAYS = 7;

// ── dates ──────────────────────────────────────────────────────────────────
export function validDate(s) {
  return typeof s === 'string' && DATE_RE.test(s) && !Number.isNaN(Date.parse(s + 'T00:00:00Z')) && new Date(s + 'T00:00:00Z').toISOString().slice(0, 10) === s;
}
export function addDays(s, n) { const t = new Date(s + 'T00:00:00Z'); t.setUTCDate(t.getUTCDate() + n); return t.toISOString().slice(0, 10); }
export function diffDays(a, b) { return Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / 86400000); }
const clampInt = (v, lo, hi, dflt) => { const n = Math.round(Number(v)); return Number.isFinite(n) ? Math.max(lo, Math.min(hi, n)) : dflt; };

// ── focus (deep link) ────────────────────────────────────────────────────
const FOCUS_IDS = ['listing_id', 'stay_id', 'turn_id', 'wo_id'];
/**
 * Validate a focus record (a JSON string from sessionStorage, or an object).
 * Returns the clean record or null. Unknown keys are dropped; a bad value on
 * a known key rejects the whole record (never half-apply a tampered link).
 */
export function parseFocus(raw, nowMs = Date.now()) {
  let o = raw;
  if (typeof raw === 'string') {
    if (!raw || raw.length > FOCUS_MAX_CHARS) return null;
    try { o = JSON.parse(raw); } catch { return null; }
  }
  if (!o || typeof o !== 'object' || Array.isArray(o)) return null;
  if (o.v !== FOCUS_VERSION) return null;
  const out = { v: FOCUS_VERSION };
  if (o.view != null) { if (typeof o.view !== 'string' || !VIEW_RE.test(o.view)) return null; out.view = o.view; }
  if (o.date != null) { if (!validDate(o.date)) return null; out.date = o.date; }
  for (const k of FOCUS_IDS) {
    if (o[k] == null) continue;
    const v = typeof o[k] === 'number' && Number.isFinite(o[k]) ? String(o[k]) : o[k];
    if (typeof v !== 'string' || !ID_RE.test(v)) return null;
    out[k] = v;
  }
  if (o.src != null) { if (typeof o.src !== 'string' || !/^[\w.-]{1,40}$/.test(o.src)) return null; out.src = o.src; }
  if (o.at != null) {
    const at = Number(o.at);
    if (!Number.isFinite(at) || at > nowMs + 60000 || nowMs - at > FOCUS_MAX_AGE_MS) return null;
    out.at = at;
  }
  if (!out.view && !out.date && !FOCUS_IDS.some(k => out[k])) return null;   // nothing to do
  return out;
}
/** The string openCalendar() stores — null when the record would not survive parseFocus. */
export function serializeFocus(f, nowMs = Date.now()) {
  if (!f || typeof f !== 'object') return null;
  const rec = { v: FOCUS_VERSION, ...f, at: nowMs };
  for (const k of Object.keys(rec)) if (rec[k] == null || rec[k] === '') delete rec[k];
  rec.v = FOCUS_VERSION; rec.at = nowMs;
  const clean = parseFocus(rec, nowMs);
  if (!clean) return null;
  const s = JSON.stringify(clean);
  return s.length <= FOCUS_MAX_CHARS ? s : null;
}

// ── views ──────────────────────────────────────────────────────────────────
/**
 * The view to show. `available` = the view ids this role may use (registered
 * presets, role-filtered). Order: the deep link's view (transient) → this
 * tab's view → the user's explicit default → 'stays' → the first available.
 */
export function resolveView({ focusView, tabView, savedView, available }) {
  const ok = new Set((available || []).filter(v => typeof v === 'string' && VIEW_RE.test(v)));
  const pick = (v, source) => (typeof v === 'string' && ok.has(v) ? { view: v, source } : null);
  return pick(focusView, 'focus') || pick(tabView, 'tab') || pick(savedView, 'default') || pick(DEFAULT_VIEW, 'fallback')
    || { view: (available && available[0]) || DEFAULT_VIEW, source: 'fallback' };
}
/** The per-user key for "Make this my default" (profile id, else email, else 'anon'). */
export function defaultViewKey(profile) {
  const p = profile && typeof profile === 'object' ? profile : {};
  const uid = String(p.id || p.email || 'anon').toLowerCase().replace(/[^\w@.-]/g, '').slice(0, 80) || 'anon';
  return `stc_default_view:${uid}`;
}
/** R115: the pre-hub one-chip filter (sessionStorage 'stc_filter') → the Reservations stat it became, or null. */
export function legacyFilterToStat(key) {
  return ({ arriving_today: 'arriving_today', negative: 'negative', open_weekend: 'open_weekend', vacant_tonight: 'vacant_tonight' })[key] || null;
}

// ── lanes (moved verbatim from StaysCalendarPage.ts) ─────────────────────
// items arrive in rank order, not start order, so a lane is free only if
// NOTHING already in it overlaps (hotel style: a bar ends at mid-day, so a
// same-day turn shares the lane and a block fits the gap between two stays)
export function packLanes(items) {
  const lanes = [];
  for (const it of items) {
    let lane = lanes.findIndex(l => l.every(iv => iv.end <= it.start || iv.start >= it.end));
    if (lane < 0) { lane = lanes.length; lanes.push([]); }
    lanes[lane].push({ start: it.start, end: it.end });
    it.lane = lane;
  }
  return lanes.length;
}
// two passes: real stays (then blocks) are laid first so the solid bars
// always hold the lowest lanes; inquiries fill whatever lanes are left
export function assignLanes(stays, blocks) {
  const items = [
    ...(stays || []).map(s => ({ kind: 'stay', stay: s, start: s.check_in, end: s.check_out, lane: 0 })),
    ...(blocks || []).map(b => ({ kind: 'block', block: b, start: b.start, end: b.end, lane: 0 })),
  ];
  const rank = (it) => (it.kind === 'block' ? 1 : (it.stay.kind === 'inquiry' ? 2 : 0));
  items.sort((a, b) => rank(a) - rank(b) || a.start.localeCompare(b.start) || a.end.localeCompare(b.end));
  packLanes(items);
  return items;
}

// ── window (B9 — lazy past) ────────────────────────────────────────────────
// win    = what the user asked for: { from: '' (anchored on yesterday) | date, days: forward columns (14|28|56), extra: past columns 0|30|60 }
// loaded = what the server answered: { from, to, days (all columns), anchor? }
/** The /api/stays/calendar query for a window: anchored → back+days; explicit → from + the same column count. */
export function windowQuery(win) {
  const days = clampInt(win && win.days, MIN_DAYS, MAX_DAYS, 28);
  const extra = clampInt(win && win.extra, 0, MAX_BACK, 0);
  if (win && win.from && validDate(win.from)) return { from: win.from, days: Math.min(MAX_DAYS, days + extra) };
  return { back: Math.max(0, Math.min(extra, MAX_DAYS - days)), days };
}
/** Columns the window will draw. */
export function windowColumns(win) { const q = windowQuery(win); return q.from ? q.days : q.days + q.back; }
/** Where the anchored window's "today − 1" column sits (the scroll target on a fresh anchored draw). */
export function anchorColumn(loaded) {
  if (!loaded || !validDate(loaded.from)) return 0;
  const anchor = validDate(loaded.anchor) ? loaded.anchor : loaded.from;
  return Math.max(0, diffDays(loaded.from, anchor));
}
/**
 * ‹ / › / Today. Inside the loaded range it only scrolls (7 columns); at an
 * edge it asks for a fetch whose window keeps the column count, with the
 * scrollLeft that keeps the motion one week: { kind:'scroll', left } |
 * { kind:'fetch', win, left }.
 *   o = { dir, win, loaded, scrollLeft, viewport (px of day columns visible), colW }
 */
export function navStep(o) {
  const colW = o.colW || 64;
  const loaded = o.loaded;
  const win = { from: o.win && o.win.from ? o.win.from : '', days: clampInt(o.win && o.win.days, MIN_DAYS, MAX_DAYS, 28), extra: clampInt(o.win && o.win.extra, 0, MAX_BACK, 0) };
  const cols = loaded && Number(loaded.days) > 0 ? Number(loaded.days) : windowColumns(win);
  const maxScroll = Math.max(0, cols * colW - Math.max(0, Number(o.viewport) || 0));
  const left = Math.max(0, Number(o.scrollLeft) || 0);
  const step = NAV_STEP * colW;
  if (o.dir === 'today') {
    if (!win.from && loaded) {   // anchored: the anchor column is already loaded — just scroll there
      return { kind: 'scroll', left: Math.min(maxScroll, anchorColumn(loaded) * colW) };
    }
    const next = { from: '', days: win.days, extra: win.extra };
    return { kind: 'fetch', win: next, left: windowQuery(next).back * colW };
  }
  if (!loaded || !validDate(loaded.from)) return { kind: 'scroll', left };
  if (o.dir === 'next') {
    if (left < maxScroll - 1) return { kind: 'scroll', left: Math.min(maxScroll, left + step) };
    // the right edge: page the window a week forward; the same scrollLeft then shows the next week
    return { kind: 'fetch', win: { from: addDays(loaded.from, NAV_STEP), days: win.days, extra: Math.max(0, cols - win.days) }, left };
  }
  // prev
  if (left > 1) return { kind: 'scroll', left: Math.max(0, left - step) };
  // the left edge of an anchored window: load the past lazily (B9) when there is room
  if (!win.from && win.extra + BACK_STEP <= MAX_BACK && win.days + win.extra + BACK_STEP <= MAX_DAYS) {
    const next = { from: '', days: win.days, extra: win.extra + BACK_STEP };
    return { kind: 'fetch', win: next, left: left + (BACK_STEP - NAV_STEP) * colW };
  }
  return { kind: 'fetch', win: { from: addDays(loaded.from, -NAV_STEP), days: win.days, extra: Math.max(0, cols - win.days) }, left };
}
/**
 * "‹ Earlier" (the edge control / ⋯ menu): 30 more past days, the visible
 * dates stay put (scrollLeft += 30 columns). null when nothing more can load.
 */
export function earlierStep(o) {
  const colW = o.colW || 64;
  const win = { from: o.win && o.win.from ? o.win.from : '', days: clampInt(o.win && o.win.days, MIN_DAYS, MAX_DAYS, 28), extra: clampInt(o.win && o.win.extra, 0, MAX_BACK, 0) };
  const left = Math.max(0, Number(o.scrollLeft) || 0);
  if (!win.from) {
    if (win.extra + BACK_STEP > MAX_BACK || win.days + win.extra + BACK_STEP > MAX_DAYS) return null;
    return { win: { from: '', days: win.days, extra: win.extra + BACK_STEP }, left: left + BACK_STEP * colW };
  }
  const loadedFrom = o.loaded && validDate(o.loaded.from) ? o.loaded.from : win.from;
  if (win.days + win.extra + BACK_STEP <= MAX_DAYS && win.extra + BACK_STEP <= MAX_BACK) return { win: { from: addDays(loadedFrom, -BACK_STEP), days: win.days, extra: win.extra + BACK_STEP }, left: left + BACK_STEP * colW };
  // full: slide the same-size window 30 days back — the dates on screen move 30 columns right, so keep them there
  return { win: { from: addDays(loadedFrom, -BACK_STEP), days: win.days, extra: win.extra }, left: left + BACK_STEP * colW };
}
/** Can "‹ Earlier" load anything more? */
export function canEarlier(win) { return earlierStep({ win, scrollLeft: 0, colW: 1 }) !== null; }
/**
 * Jump to a date: inside the loaded range → just scroll to it; otherwise an
 * explicit window that starts the day before it (same column count).
 */
export function jumpWindow(o) {
  if (!validDate(o.date)) return null;
  const colW = o.colW || 64;
  const loaded = o.loaded;
  const win = { from: o.win && o.win.from ? o.win.from : '', days: clampInt(o.win && o.win.days, MIN_DAYS, MAX_DAYS, 28), extra: clampInt(o.win && o.win.extra, 0, MAX_BACK, 0) };
  if (loaded && validDate(loaded.from) && validDate(loaded.to) && o.date >= loaded.from && o.date <= loaded.to) {
    return { kind: 'scroll', left: Math.max(0, diffDays(loaded.from, o.date) - 1) * colW };
  }
  return { kind: 'fetch', win: { from: addDays(o.date, -1), days: win.days, extra: win.extra }, left: 0 };
}

// ── stats over the forward window ─────────────────────────────────────────
/** The first day window counts look at: max(from, today−1) — or `from` when the whole window is past. */
export function forwardStart(from, to, asOf) {
  const y = addDays(asOf, -1);
  const start = from > y ? from : y;
  return start > to ? from : start;
}
const overlaps = (s, a, b) => s.check_out >= a && s.check_in <= b;
/**
 * The Reservations stats, computed from the payload the way the server does
 * (an older server without `filters` / `kpis` still gets honest numbers):
 * today-based KPIs + window counts over the FORWARD window only.
 */
export function coreStatCounts(d) {
  const today = d.as_of;
  const stays = Array.isArray(d.stays) ? d.stays : [];
  const props = Array.isArray(d.properties) ? d.properties : [];
  const fwd = forwardStart(d.from, d.to, today);
  const real = stays.filter(s => s.kind !== 'inquiry');
  const k = d.kpis || {};
  const f = d.filters || {};
  const num = (v, fb) => (Number.isFinite(Number(v)) && v !== null && v !== '' ? Number(v) : fb);
  return {
    arriving_today: num(k.check_ins_today, real.filter(s => s.check_in === today).length),
    checkouts_today: num(k.check_outs_today, real.filter(s => s.check_out === today).length),
    in_house: num(k.staying_tonight, real.filter(s => s.check_in <= today && s.check_out > today).length),
    vacant_tonight: num(f.vacant_tonight, props.filter(p => p.vacant_tonight === true).length),
    negative: num(f.negative, stays.filter(s => s.negative === true && overlaps(s, fwd, d.to)).length),
    open_weekend: num(f.open_weekend, props.filter(p => !!(p.open_weekend && p.open_weekend.fri)).length),
    negative_in_house: num(k.negative_in_house, real.filter(s => s.negative === true && s.check_in <= today && s.check_out >= today).length),
  };
}

/** The layer cache key — one entry per layer per loaded window. */
export function layerKey(id, from, to) { return `${id}|${from}|${to}`; }
